import { Settings } from '@mui/icons-material';
import { Control, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, Switch } from '@mui/material';
import { Autocomplete, Button, IconTitle, Input } from '../../../../../components';
import Market, { MarketOperator, MarketType, PaymentMethod } from '../../../../../entities/market.entity';
import useStyles from './styles';
import { Condominium, Contractor } from '../../../../../entities';
import { MarketForm, schema } from '../../../../../validations/market.validation';
import { RecursiveKeyOf } from '../../../../../utils/types';
import { clone } from '../../../../../utils/helpers';
import CheckboxGroup from '../../../../../components/CheckBoxGroup';

interface FormProps {
  market: Market;
  condominium: Condominium[];
  contractor: Contractor[];
  onSubmit: (input: Market | undefined) => unknown;
  handleFieldChange: (target: RecursiveKeyOf<Market>, value: string | boolean | number | null | string[]) => void;
}

const operatorOptions = [MarketOperator.BestMarket, MarketOperator.Box247, MarketOperator.Quitandinha];
const marketTypeOptions = [MarketType.External, MarketType.Prestashop];
const paymentOptions = [PaymentMethod.CreditCard, PaymentMethod.Pix];

const optionOperatorLabel: Record<MarketOperator, string> = {
  [MarketOperator.BestMarket]: 'Best Market',
  [MarketOperator.Box247]: 'Box 247',
  [MarketOperator.Quitandinha]: 'Quitandinha',
};

const optionMarketLabel: Record<MarketType, string> = {
  [MarketType.External]: 'Externo',
  [MarketType.Prestashop]: 'Prestashop',
};

const optionPaymentLabel: Record<PaymentMethod, string> = {
  [PaymentMethod.CreditCard]: 'cartão de crédito',
  [PaymentMethod.Pix]: 'pix',
};

export const Form = ({ market, condominium, contractor, onSubmit, handleFieldChange }: FormProps): JSX.Element => {
  const styles = useStyles();
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<MarketForm>({
    resolver: yupResolver(schema),
    defaultValues: market,
  });

  const currentType = watch('type');
  const currentCondominium = watch('condominium');
  const currentOperator = watch('operator');
  const currentContractor = watch('contractor');

  const validationRegister = useCallback(
    (name: RecursiveKeyOf<MarketForm>, isNumber?: boolean) =>
      register(name as keyof MarketForm, {
        onChange: (e) => handleFieldChange(name, isNumber ? Number(e.target.value) : e.target.value),
      }),
    [register, handleFieldChange]
  );

  useEffect(() => {
    reset(clone(market));
  }, [reset, market]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.right}>
            <div className={styles.line}>
              <Input
                placeholder="Mercadinho"
                label="mercado*"
                validationRegister={validationRegister('label')}
                fullWidth
                error={Boolean(errors.label?.message)}
                helperText={errors.label?.message || ''}
              />
              <Autocomplete
                label="condominio*"
                value={condominium.find(({ id }) => id === currentCondominium) || null}
                placeholder="Selecione o condomínio"
                options={condominium}
                getOptionLabel={(opt) => opt.name}
                fullWidth
                onChange={(_, newValue) => {
                  setValue('condominium', newValue?.id!, { shouldDirty: true });
                  handleFieldChange('condominium', newValue?.id!);
                }}
                error={Boolean(errors.condominium?.message)}
                helperText={errors.condominium?.message || ''}
              />
              <Input
                label="código do mercado"
                validationRegister={validationRegister('marketCode')}
                control={control as unknown as Control}
                fullWidth
                placeholder="DHSOWD"
                error={Boolean(errors.marketCode?.message)}
                helperText={errors.marketCode?.message || ''}
              />
              <Autocomplete
                label="tipo*"
                placeholder="Selecione o tipo"
                value={marketTypeOptions.find((opt) => opt === currentType) || null}
                getOptionLabel={(opt) => optionMarketLabel[opt]}
                options={marketTypeOptions}
                fullWidth
                onChange={(_, newValue) => {
                  setValue('type', newValue!, { shouldDirty: true });
                  handleFieldChange('type', newValue);
                }}
              />
            </div>
            <div className={styles.line}>
              <Autocomplete
                label="operador"
                placeholder="Selecione o operador"
                value={operatorOptions.find((opt) => opt === currentOperator) || null}
                getOptionLabel={(opt) => optionOperatorLabel[opt]}
                options={operatorOptions}
                fullWidth
                onChange={(_, newValue) => {
                  setValue('operator', newValue!, { shouldDirty: true });
                  handleFieldChange('operator', newValue);
                }}
              />
              <Autocomplete
                label="parceiro"
                value={contractor.find(({ id }) => id === currentContractor) || null}
                placeholder="Selecione um parceiro"
                options={contractor}
                getOptionLabel={(opt) => opt.label}
                fullWidth
                onChange={(_, newValue) => {
                  setValue('contractor', newValue?.id!, { shouldDirty: true });
                  handleFieldChange('contractor', newValue?.id!);
                }}
              />

              <FormControlLabel
                label="sincronização com parceiro"
                control={
                  <Switch
                    value={market.isSyncEnable ?? false}
                    onChange={(_, newValue) => {
                      handleFieldChange('isSyncEnable', newValue);
                    }}
                  />
                }
              />
            </div>
            <div className={styles.line}>
              <CheckboxGroup
                label="métodos de pagamento"
                options={paymentOptions.map((opt) => ({
                  name: opt,
                  label: optionPaymentLabel[opt],
                  checked: market.paymentMethods?.includes(opt),
                }))}
                onChange={(selected) => {
                  const selectedItems = Object.keys(selected).filter((key) => selected[key]);
                  handleFieldChange('paymentMethods', selectedItems);
                }}
                helperText="Selecione as opções de pagamento"
                layout="horizontal"
                key={market.paymentMethods?.join(',')}
                required
                minSelected={1}
              />
            </div>
            {currentType === MarketType.External ? (
              <></>
            ) : (
              <>
                <IconTitle title="Prestashop" IconComponent={Settings} />
                <div className={styles.line}>
                  <Input
                    placeholder="https://prestashop.com"
                    label="url*"
                    validationRegister={validationRegister('prestashopBaseUrl')}
                    fullWidth
                    error={Boolean(errors.prestashopBaseUrl?.message)}
                    helperText={errors.prestashopBaseUrl?.message || ''}
                  />
                  <Input
                    placeholder="85THHMAJ7ZWUTUCHQC2SIX3JRXR242DS"
                    label="token*"
                    validationRegister={validationRegister('prestashopAuthToken')}
                    fullWidth
                    error={Boolean(errors.prestashopAuthToken?.message)}
                    helperText={errors.prestashopAuthToken?.message || ''}
                  />
                </div>
                <div className={styles.line}>
                  <Input
                    placeholder="1"
                    label="id da mercado*"
                    validationRegister={validationRegister('prestashopShopId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopShopId?.message)}
                    helperText={errors.prestashopShopId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id de endereço*"
                    validationRegister={validationRegister('prestashopAddressId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopAddressId?.message)}
                    helperText={errors.prestashopAddressId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id da transportadora*"
                    validationRegister={validationRegister('prestashopCarrierId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopCarrierId?.message)}
                    helperText={errors.prestashopCarrierId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id da categoria*"
                    validationRegister={validationRegister('prestashopCategoryParentId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopCategoryParentId?.message)}
                    helperText={errors.prestashopCategoryParentId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id da moeda*"
                    validationRegister={validationRegister('prestashopCurrencyId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopCurrencyId?.message)}
                    helperText={errors.prestashopCurrencyId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id grupo da loja*"
                    validationRegister={validationRegister('prestashopGroupShopId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopGroupShopId?.message)}
                    helperText={errors.prestashopGroupShopId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id da linguagem*"
                    validationRegister={validationRegister('prestashopLangId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopLangId?.message)}
                    helperText={errors.prestashopLangId?.message || ''}
                  />
                  <Input
                    placeholder="ps_cashondelivery"
                    label="id do módulo*"
                    validationRegister={validationRegister('prestashopModuleId')}
                    fullWidth
                    error={Boolean(errors.prestashopModuleId?.message)}
                    helperText={errors.prestashopModuleId?.message || ''}
                  />
                </div>
                <div className={styles.line}>
                  <Input
                    placeholder="1"
                    label="id status de falha de pagamento*"
                    validationRegister={validationRegister('prestashopPaymentFailedStateId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopPaymentFailedStateId?.message)}
                    helperText={errors.prestashopPaymentFailedStateId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id status de pagamento*"
                    validationRegister={validationRegister('prestashopPaidStateId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopPaidStateId?.message)}
                    helperText={errors.prestashopPaidStateId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id status de aguardando pagamento*"
                    validationRegister={validationRegister('prestashopWaitingPaymentStateId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopWaitingPaymentStateId?.message)}
                    helperText={errors.prestashopWaitingPaymentStateId?.message || ''}
                  />
                  <Input
                    placeholder="1"
                    label="id status de cancelamento*"
                    validationRegister={validationRegister('prestashopCanceledStateId', true)}
                    type="number"
                    fullWidth
                    error={Boolean(errors.prestashopCanceledStateId?.message)}
                    helperText={errors.prestashopCanceledStateId?.message || ''}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <Button sx={{ width: '200px' }} loading={isSubmitting} type="submit">
            Salvar
          </Button>
        </div>
      </div>
    </form>
  );
};

export default Form;
